import React from 'react';
import { Group, Line, Arc, Rect } from 'react-konva';
import { wallAngle } from './floorPlanUtils';

function FloorPlanOpening({ opening, wall, isSelected, onSelect, zoom, activeTool }) {
  if (!wall) return null;

  const angle = wallAngle(wall);
  const angleDeg = (angle * 180) / Math.PI;

  // Position along the wall
  const x = wall.x1 + (wall.x2 - wall.x1) * opening.position;
  const y = wall.y1 + (wall.y2 - wall.y1) * opening.position;

  const halfWidth = opening.width / 2;
  const wallThick = wall.thickness || 6;

  if (opening.type === 'door') {
    const swingDir = opening.direction === 'right' ? 1 : -1;
    const swingIn = opening.swing === 'in';
    const swingSign = swingIn ? 1 : -1;

    // Hinge position
    const hingeX = opening.direction === 'left' ? -halfWidth : halfWidth;

    // Door leaf endpoint
    const leafEndX = opening.direction === 'left'
      ? -halfWidth + opening.width * swingDir
      : halfWidth - opening.width * swingDir;
    const leafEndY = swingSign * opening.width * 0.7;

    // Arc rotation: determines where the 90-degree arc starts
    let arcRotation;
    if (opening.direction === 'left') {
      arcRotation = swingIn ? 0 : -90;
    } else {
      arcRotation = swingIn ? 90 : 180;
    }

    // Selection highlight bounds
    const highlightY = swingIn ? -wallThick / 2 : -opening.width * 0.8;
    const highlightH = swingIn
      ? opening.width * 0.8 + wallThick / 2 + 3 / zoom
      : opening.width * 0.8 + wallThick / 2 + 3 / zoom;

    return (
      <Group
        x={x}
        y={y}
        rotation={angleDeg}
        onClick={(e) => {
          if (activeTool !== 'select') return;
          e.cancelBubble = true;
          onSelect(opening.id);
        }}
        onTap={(e) => {
          if (activeTool !== 'select') return;
          e.cancelBubble = true;
          onSelect(opening.id);
        }}
      >
        {/* Wall break (white gap) */}
        <Rect
          x={-halfWidth}
          y={-wallThick / 2}
          width={opening.width}
          height={wallThick}
          fill="#FFFFFF"
        />

        {/* Door leaf */}
        <Line
          points={[hingeX, 0, leafEndX, leafEndY]}
          stroke={isSelected ? '#C4612E' : '#555'}
          strokeWidth={2 / zoom}
        />

        {/* Door arc */}
        <Arc
          x={hingeX}
          y={0}
          innerRadius={opening.width * 0.68}
          outerRadius={opening.width * 0.72}
          angle={90}
          rotation={arcRotation}
          stroke={isSelected ? '#C4612E' : '#999'}
          strokeWidth={1 / zoom}
        />

        {/* Selection highlight */}
        {isSelected && (
          <Rect
            x={-halfWidth - 3 / zoom}
            y={highlightY}
            width={opening.width + 6 / zoom}
            height={highlightH}
            stroke="#C4612E"
            strokeWidth={1 / zoom}
            dash={[4 / zoom, 4 / zoom]}
            fill="transparent"
          />
        )}
      </Group>
    );
  }

  // Window
  return (
    <Group
      x={x}
      y={y}
      rotation={angleDeg}
      onClick={(e) => {
        if (activeTool !== 'select') return;
        e.cancelBubble = true;
        onSelect(opening.id);
      }}
      onTap={(e) => {
        if (activeTool !== 'select') return;
        e.cancelBubble = true;
        onSelect(opening.id);
      }}
    >
      {/* Wall break */}
      <Rect
        x={-halfWidth}
        y={-wallThick / 2}
        width={opening.width}
        height={wallThick}
        fill="#FFFFFF"
      />

      {/* Window lines (double line pattern) */}
      <Line
        points={[-halfWidth, -wallThick / 4, halfWidth, -wallThick / 4]}
        stroke={isSelected ? '#C4612E' : '#4A90D9'}
        strokeWidth={1.5 / zoom}
      />
      <Line
        points={[-halfWidth, wallThick / 4, halfWidth, wallThick / 4]}
        stroke={isSelected ? '#C4612E' : '#4A90D9'}
        strokeWidth={1.5 / zoom}
      />

      {/* Window end marks */}
      <Line
        points={[-halfWidth, -wallThick / 2, -halfWidth, wallThick / 2]}
        stroke={isSelected ? '#C4612E' : '#4A90D9'}
        strokeWidth={1.5 / zoom}
      />
      <Line
        points={[halfWidth, -wallThick / 2, halfWidth, wallThick / 2]}
        stroke={isSelected ? '#C4612E' : '#4A90D9'}
        strokeWidth={1.5 / zoom}
      />

      {/* Selection highlight */}
      {isSelected && (
        <Rect
          x={-halfWidth - 3 / zoom}
          y={-wallThick / 2 - 3 / zoom}
          width={opening.width + 6 / zoom}
          height={wallThick + 6 / zoom}
          stroke="#C4612E"
          strokeWidth={1 / zoom}
          dash={[4 / zoom, 4 / zoom]}
          fill="transparent"
        />
      )}
    </Group>
  );
}

export default React.memo(FloorPlanOpening);
